import type { Row, SortingState } from "@tanstack/react-table";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import clsx from "clsx";
import DraftProjectInvoiceDetailsSidebar
	from "modules/billing/components/DraftInvoicesIndexView/components/DraftProjectInvoiceDetailsSidebar";
import type { MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import Table from "~/components/Table";
import { INVOICES_DRAFT_PAGE_KEY, INVOICES_SHARED_FILTER_VALUES } from "~/constants/pageStateStorageKeys.ts";
import { useAuth } from "~/contexts/AuthContext";
import usePageStateStorage from "~/hooks/usePageStateStorage";
import type { UnprocessedInvoicesIndexResponse } from "~/modules/billing/api/unprocessedInvoicesTypes.ts";
import {
	applyDeliveryPeriodFilter,
	castFilterDatesToDate,
} from "~/modules/billing/components/DeliveryPeriodFilter/deliveryPeriodFilterUtils.ts";
import DraftNonProjectInvoiceDetailsSidebar
	from "~/modules/billing/components/DraftInvoicesIndexView/components/DraftNonProjectInvoiceDetailsSidebar";
import UpdateDraftInvoiceCommentSidebar
	from "~/modules/billing/components/DraftInvoicesIndexView/components/UpdateDraftInvoiceCommentSidebar";
import type {
	SelectedDraftInvoiceData,
} from "~/modules/billing/components/DraftInvoicesIndexView/draftInvoiceIndexViewTypes.ts";
import {
	draftInvoicesIndexTableColumnDefs,
} from "~/modules/billing/components/DraftInvoicesIndexView/draftInvoicesTableColumnDefs.tsx";
import InvoicePageHeader from "~/modules/billing/components/InvoicePageHeader";
import type {
	InvoiceCommentData,
	UnprocessedInvoicesIndexTableData,
	UnprocessedInvoicesIndexTableMeta,
} from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import {
	createUnprocessedInvoiceTableDataFromNonProjectInvoice,
	createUnprocessedInvoiceTableDataFromProjectInvoice,
	InvoicePageTabId,
	invoiceTablesDefaultPageState,
	invoiceTableSharedFilterDefaultPageState,
} from "~/modules/billing/utils/billingUtils.ts";
import type { DateRangeNullable } from "~/types/dateAndTimeTypes.ts";
import { PermissionNames } from "~/types/entityNames.ts";

type Props = {
	isLoading: boolean,
	draftInvoicesData?: UnprocessedInvoicesIndexResponse
};
const DraftInvoicesIndexView = ({ isLoading, draftInvoicesData }: Props) => {
	const { hasAnyPermission } = useAuth();
	const userCanManageInvoices = hasAnyPermission([PermissionNames.CanManageInvoices]);
	const { pageState, setPageState } = usePageStateStorage({
		pageKey: INVOICES_DRAFT_PAGE_KEY,
		defaultState: invoiceTablesDefaultPageState,
	});

	const { pageState: sharedFilterPageState, setPageState: setSharedFilterPageState } = usePageStateStorage({
		pageKey: INVOICES_SHARED_FILTER_VALUES,
		defaultState: invoiceTableSharedFilterDefaultPageState,
	});
	const [deliveryPeriodFilter, setDeliveryPeriodFilter] = useState<DateRangeNullable>(castFilterDatesToDate(sharedFilterPageState.deliveryPeriodFilter));
	const [globalFilter, setGlobalFilter] = useState<string>(sharedFilterPageState.globalFilter);
	const [sorting, setSorting] = useState<SortingState>(pageState.sorting);

	useEffect(() => {
		setPageState({ sorting });
	}, [setPageState, sorting]);

	useEffect(() => {
		setSharedFilterPageState({ globalFilter, deliveryPeriodFilter });
	}, [deliveryPeriodFilter, globalFilter, setSharedFilterPageState]);

	const [invoiceCommentToUpdate, setInvoiceCommentToUpdate] = useState<InvoiceCommentData | undefined>();
	const [invoiceToShowDetailsOf, setInvoiceToShowDetailsOf] = useState<SelectedDraftInvoiceData | undefined>();


	const tableData: UnprocessedInvoicesIndexTableData[] = useMemo(() => {
		if (draftInvoicesData) {
			const projectInvoices = draftInvoicesData.projectInvoices.map((invoice) => {
				return createUnprocessedInvoiceTableDataFromProjectInvoice(invoice, draftInvoicesData.projectPhases, draftInvoicesData.projects);
			});
			return [...projectInvoices, ...draftInvoicesData.otherInvoices.map((invoice) => {
				return createUnprocessedInvoiceTableDataFromNonProjectInvoice(invoice);
			})];
		}
		return [];
	}, [draftInvoicesData]);

	const preFilteredTableData = useMemo(() => {
		return applyDeliveryPeriodFilter(deliveryPeriodFilter, tableData);
	}, [tableData, deliveryPeriodFilter]);


	const handleRowClick = useCallback((row: Row<UnprocessedInvoicesIndexTableData>) => {
		setInvoiceToShowDetailsOf({ invoiceId: row.original.id, isProjectInvoice: row.original.isProjectInvoice });
	}, []);

	const handleUpdateCommentClick = useCallback((event: MouseEvent, row: Row<UnprocessedInvoicesIndexTableData>) => {
		event.stopPropagation();
		setInvoiceCommentToUpdate({
			invoiceId: row.original.id,
			comment: row.original.comment,
			isProjectInvoice: row.original.isProjectInvoice,
		});
	}, []);

	const table = useReactTable({
		data: preFilteredTableData,
		columns: draftInvoicesIndexTableColumnDefs,
		onSortingChange: setSorting,
		state: {
			globalFilter,
			sorting,
		},
		meta: {
			onRowClick: handleRowClick,
			onUpdateCommentClick: handleUpdateCommentClick,
		} as UnprocessedInvoicesIndexTableMeta,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		sortDescFirst: true,
	});

	return <div className={clsx("auto-grid-2-rows mx-auto h-screen px-8")}>
		<InvoicePageHeader activeTabId={InvoicePageTabId.Draft}
						   deliveryPeriodFilter={deliveryPeriodFilter}
						   globalFilter={globalFilter}
						   setDeliveryPeriodFilter={setDeliveryPeriodFilter}
						   setGlobalFilter={setGlobalFilter}
						   userCanManageInvoices={userCanManageInvoices}
		>
		</InvoicePageHeader>
		<div className="h-full space-y-8 overflow-y-auto pb-10">
			<Table table={table}
				   isLoading={isLoading}
				   fixedWidth={true} />
		</div>
		<DraftProjectInvoiceDetailsSidebar invoiceId={invoiceToShowDetailsOf?.invoiceId}
										   isOpen={!!invoiceToShowDetailsOf && invoiceToShowDetailsOf.isProjectInvoice}
										   onClose={() => setInvoiceToShowDetailsOf(undefined)}
										   userCanManageInvoices={userCanManageInvoices}
		/>
		<DraftNonProjectInvoiceDetailsSidebar invoiceId={invoiceToShowDetailsOf?.invoiceId}
											  isOpen={!!invoiceToShowDetailsOf && !invoiceToShowDetailsOf.isProjectInvoice}
											  onClose={() => setInvoiceToShowDetailsOf(undefined)}
											  userCanManageInvoices={userCanManageInvoices}
		/>
		<UpdateDraftInvoiceCommentSidebar isOpen={!!invoiceCommentToUpdate}
										  onClose={() => setInvoiceCommentToUpdate(undefined)}
										  invoiceCommentData={invoiceCommentToUpdate}
										  userCanManageInvoices={userCanManageInvoices}
		/>
	</div>;
};

export default DraftInvoicesIndexView;